// src/App.tsx
import { useEffect, useRef, useState } from "react";
import "../Report.css";
import CoverPage from "../common/CoverPage";
import { IntroPage } from "./IntroPage";
import { ResumenEjectutivoPage } from "./ResumenEjectutivoPage";
import { prepareDisplayDate } from "@/lib/utils";
import { datesAtom } from "@/store/dates";
import { useAtom } from "jotai";
import BackPage from "../common/BackPage";
import { ComparativaPage } from "./ComparativaPage";
import { VehiculosPropiosPage } from "./VehiculosPropiosPage";
import { SedesPage } from "./SedesPage";
import { companyAtom } from "@/store/company";
import { useAuth } from "@/hooks/useAuth";
import { useQuery } from "@tanstack/react-query";
import { getApiUrl } from "@/utils/api";
import { useTranslation } from "react-i18next";
import { categoriesAtom } from "@/store/categories";

export const Base = () => {
  const { t } = useTranslation();
  const reportRef = useRef<HTMLDivElement>(null);
  const [company] = useAtom(companyAtom);
  const [dates] = useAtom(datesAtom);
  const [categories] = useAtom(categoriesAtom);
  const [downloading, setDownloading] = useState<boolean>(false);
  const { token } = useAuth();

  const [currentRenderingPage, setCurrentRenderingPage] = useState<
    number | null
  >(null);
  const [showVehicles, setShowVehicles] = useState<boolean>(true);

  const { data: facilitiesData } = useQuery<any>({
    queryKey: ["facilities", company?.id],
    queryFn: async () => {
      const response = await fetch(
        getApiUrl(`facilities/?company=${company?.id}`),
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );
      if (!response.ok) {
        throw new Error("Failed to fetch data types");
      }
      return response.json();
    },
    enabled: !!token && !!company,
  });

  // Calculate how many pages SedesPage will occupy
  const sedesPagesCount = facilitiesData?.count || 1;

  const handleDownloadPdf = async () => {
    setDownloading(true);
    setCurrentRenderingPage(0);
    console.log("Starting final, manual PDF generation...");

    // Find all company logo images and store their original URLs
    const imageElements = document.querySelectorAll<HTMLImageElement>(
      'img[alt="Company Logo"]',
    );
    const originalSrcs: (string | null)[] = [];

    // Convert images to base64 to bypass CORS issues with html2canvas
    for (let i = 0; i < imageElements.length; i++) {
      const img = imageElements[i];
      originalSrcs[i] = img.src;
      // Proceed only if the src is a URL and not already a data URI
      if (img.src && !img.src.startsWith("data:")) {
        try {
          const response = await fetch(img.src, { mode: "cors" }); // Explicitly set mode to 'cors'
          if (!response.ok) {
            throw new Error(
              `Failed to fetch image: ${response.status} ${response.statusText}`,
            );
          }
          const blob = await response.blob();
          console.log("Blob size:", blob.size);
          const reader = new FileReader();
          // Use a Promise to wait for the reader to finish
          await new Promise<void>((resolve, reject) => {
            reader.onloadend = () => {
              img.src = reader.result as string;
              resolve();
            };
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });
        } catch (error) {
          console.error("Error converting image to base64:", error);
          // If conversion fails, do nothing and let html2canvas try with the original src
        }
      }
    }

    const pages = document.querySelectorAll<HTMLElement>(".pdf-page");
    if (pages.length === 0) {
      console.error("No .pdf-page elements found.");
      // Restore original sources even if PDF generation fails
      imageElements.forEach((img, i) => {
        img.src = originalSrcs[i] || "";
      });
      return;
    }

    // Dynamically import libraries to ensure they are loaded.
    const { default: jsPDF } = await import("jspdf");
    const { default: html2canvas } = await import("html2canvas");

    // 1. Create a new jsPDF instance.
    const pdf = new jsPDF({
      orientation: "landscape",
      unit: "mm",
      format: "a4",
    });

    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();

    // 2. Iterate over each page, render it to a canvas, and add it to the PDF.
    for (let i = 0; i < pages.length; i++) {
      const page = pages[i];
      setCurrentRenderingPage(i + 1);
      console.log(`Processing page ${currentRenderingPage}...`);
      const canvas = await html2canvas(page, {
        scale: 2, // Higher scale for better quality
        useCORS: true,
      });

      const imgData = canvas.toDataURL("image/jpeg", 0.98);

      // Add the image to the PDF. If it's not the first page, add a new page first.
      if (i > 0) {
        pdf.addPage();
      }
      pdf.addImage(imgData, "JPEG", 0, 0, pdfWidth, pdfHeight);
    }

    // 3. Save the final PDF.
    pdf.save("ECCO_Network_Estandard_Report.pdf");
    console.log("PDF generation complete!");

    // Restore original image sources after PDF generation is complete
    imageElements.forEach((img, i) => {
      img.src = originalSrcs[i] || "";
    });
    setDownloading(false);
    setCurrentRenderingPage(null);
  };

  const { data: categoryVehicleData } = useQuery<any>({
    queryKey: ["categories"],
    queryFn: async () => {
      const response = await fetch(getApiUrl(`categories/?name=vehicles`), {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) {
        throw new Error("Failed to fetch data types");
      }
      return response.json();
    },
    enabled: !!token,
  });

  useEffect(() => {
    if (categories && categoryVehicleData?.results) {
      setShowVehicles(categories.includes(categoryVehicleData.results[0].id));
    }
  }, [categories, categoryVehicleData]);

  return (
    <div className="App mb-8 relative">
      {/* <div className="no-print fixed right-5 top-[120px] z-[1000] rounded-md bg-black p-2.5"> */}
      <div className="w-full py-8 flex justify-center items-center gap-8 sticky top-0 bg-gray-200 z-[999]">
        <button
          onClick={handleDownloadPdf}
          disabled={downloading}
          className="cursor-pointer rounded-md border-none bg-primary px-5 py-2.5 font-bold text-black
          disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-black/50 disabled:text-white
          hover:bg-primary-foreground hover:text-white"
        >
          {downloading
            ? `${t("common.generate")}... ${currentRenderingPage}`
            : t("common.download")}
        </button>
        <button
          className="cursor-pointer rounded-md border-none bg-primary px-5 py-2.5 font-bold text-black
          hover:bg-primary-foreground hover:text-white"
          onClick={() => window.close()}
        >
          {t("common.close")}
        </button>
      </div>
      {/* Contenedor principal para el contenido del PDF */}
      <div ref={reportRef} className="pdf-container">
        <CoverPage />
        <IntroPage page={1} title={t("Introducción y metodología")} />
        <ResumenEjectutivoPage
          page={2}
          title={`${t("Resumen Ejecutivo período")} ${prepareDisplayDate(dates?.fromDate)?.toLocaleDateString()} a ${prepareDisplayDate(dates?.toDate)?.toLocaleDateString()}`}
        />
        <ComparativaPage
          page={3}
          title={`${t("Comparativa alcances y categorías período")} ${new Date(dates?.fromDate || "").getFullYear() - 1} / ${new Date(dates?.fromDate || "").getFullYear()}`}
          // title={`${t("Comparativa alcances y categorías período")} ${prepareDisplayDate(dates?.fromDate)?.toLocaleDateString()} a ${prepareDisplayDate(dates?.toDate)?.toLocaleDateString()}`}
        />
        <SedesPage
          page={4}
          title={`${t("Sedes/Instalaciones período")} ${prepareDisplayDate(dates?.fromDate)?.toLocaleDateString()} a ${prepareDisplayDate(dates?.toDate)?.toLocaleDateString()}`}
        />
        {showVehicles && (
          <VehiculosPropiosPage
            page={4 + sedesPagesCount}
            title={`${t("Vehículos propios período")} ${prepareDisplayDate(dates?.fromDate)?.toLocaleDateString()} a ${prepareDisplayDate(dates?.toDate)?.toLocaleDateString()}`}
          />
        )}
        <BackPage />
      </div>
    </div>
  );
};
